import React, { Suspense, lazy, useCallback, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/basic/Button/Button';
import Tooltip from 'components/basic/tooltip/Tooltip';
import { useOwnVaultQuery } from 'components/vaults/shared/utils';
import DisabledActionModal from 'components/wallet/mobileSync/mobile/DisabledActionModal';
import { redirectTo } from 'lib/redirect';
import { useSelector } from 'reduxState/store';

import { useDepositDisabledMessage } from './deposit/depositDisabledMessages';
import { useWithdrawDisabledMessage } from './withdraw/withdrawDisabledMessages';

const WithdrawModal = lazy(
  () => import('components/balances/withdraw/WithdrawModal')
);

const DepositModal = lazy(
  () => import('components/balances/deposit/DepositModal')
);

const Flex = styled.div`
  display: flex;
  gap: 8px;
  > div {
    flex: 1;
  }
  .isMobileStyle & {
    width: 100%;
  }
`;

function DepositWithdrawalButtons({
  underEachOther,
}: {
  underEachOther?: boolean;
}) {
  const walletType = useSelector(state => state.wallet.walletType);
  const isMobile = useSelector(state => state.screen.isMobile);
  const withdrawEnabled = useSelector(
    state => state.exchange.info.withdrawEnabled
  );
  const ownVault = useOwnVaultQuery();

  const [actionDisabledModal, setActionDisabledModal] = useState(false);

  const [modal, setModal] = useState<'none' | 'withdraw' | 'deposit'>('none');

  const handleClose = useCallback(() => {
    setModal('none');
  }, []);

  const handleWithdraw = useCallback(() => {
    // if (!walletAddress) {
    //   staticDispatch(showWalletModal());
    //   return;
    // }
    if (walletType === 'qrSynced') {
      setActionDisabledModal(true);
      return;
    }
    if (isMobile) {
      redirectTo('/mobile-withdraw');
    } else {
      setModal('withdraw');
    }
  }, [isMobile, walletType]);

  const handleDeposit = useCallback(() => {
    if (walletType === 'qrSynced') {
      setActionDisabledModal(true);
      return;
    }
    if (isMobile) {
      redirectTo('/mobile-deposit');
    } else {
      setModal('deposit');
    }
  }, [isMobile, walletType]);

  const depositDisabledMessage = useDepositDisabledMessage();
  const withdrawDisabledMessage = useWithdrawDisabledMessage();

  if (ownVault) {
    return null;
  }

  return actionDisabledModal ? (
    <DisabledActionModal
      action="depositWithdraw"
      onClose={() => setActionDisabledModal(false)}
    />
  ) : underEachOther ? (
    <>
      <Suspense
        fallback={
          <Button bordered widePadding disabled>
            Deposit
          </Button>
        }
      >
        {modal === 'deposit' && <DepositModal onClose={handleClose} />}
        {!isMobile && depositDisabledMessage ? (
          <Tooltip text={depositDisabledMessage}>
            <Button bordered widePadding disabled>
              Deposit
            </Button>
          </Tooltip>
        ) : (
          <Button onClick={handleDeposit} bordered widePadding>
            Deposit
          </Button>
        )}
      </Suspense>
      <div style={{ height: 4 }} />
      <Suspense
        fallback={
          <Button bordered widePadding disabled>
            Withdraw
          </Button>
        }
      >
        {modal === 'withdraw' && <WithdrawModal onClose={handleClose} />}
        {!isMobile && withdrawDisabledMessage ? (
          <Tooltip text={withdrawDisabledMessage}>
            <Button bordered widePadding disabled>
              Withdraw
            </Button>
          </Tooltip>
        ) : (
          <Button
            onClick={handleWithdraw}
            bordered
            widePadding
            disabled={!withdrawEnabled}
          >
            Withdraw
          </Button>
        )}
      </Suspense>
    </>
  ) : (
    <>
      <Flex>
        <div>
          <Suspense
            fallback={
              <Button bordered widePadding={!isMobile} disabled>
                Withdraw
              </Button>
            }
          >
            {modal === 'withdraw' && <WithdrawModal onClose={handleClose} />}
            {!isMobile && withdrawDisabledMessage ? (
              <Tooltip text={withdrawDisabledMessage}>
                <Button bordered widePadding disabled>
                  Withdraw
                </Button>
              </Tooltip>
            ) : (
              <Button
                onClick={handleWithdraw}
                bordered
                widePadding={!isMobile}
                disabled={!withdrawEnabled}
              >
                Withdraw
              </Button>
            )}
          </Suspense>
        </div>
        <div>
          <Suspense
            fallback={
              <Button widePadding={!isMobile} disabled>
                Deposit
              </Button>
            }
          >
            {modal === 'deposit' && <DepositModal onClose={handleClose} />}
            {!isMobile && depositDisabledMessage ? (
              <Tooltip text={depositDisabledMessage}>
                <Button widePadding disabled>
                  Deposit
                </Button>
              </Tooltip>
            ) : (
              <Button onClick={handleDeposit} widePadding={!isMobile}>
                Deposit
              </Button>
            )}
          </Suspense>
        </div>
      </Flex>
    </>
  );

  //     ) : state.activeAction === 'transfer' ? (
  //       <TransferAction
  //         ethereumWalletBalance={state.walletBalances[nativeAssetSymbol]}
  //         token={modalActiveToken}
  //         tokenWalletBalance={
  //           state.walletBalances
  //             ? state.walletBalances[modalActiveToken.symbol] || '0'
  //             : '0'
  //         }
  //         onClose={() => dispatch({ type: 'clearActiveAction' })}
  //       />
}

export default DepositWithdrawalButtons;
